<script setup lang="ts">
import AppSelect from '@/components/ui/AppSelect.vue'
import AppDateInput from '@/components/ui/AppDateInput.vue'
import { onBeforeMount, ref } from 'vue'
import { getNotAvailableDays } from '@/services/remote-diagnose'
import type { UnavailableDays } from '@/types/schedule-appointment'
import { useLang } from '@/composables/use-lang'
import AppointmentDateTimePickerContent from '@/lang/date-time-picker.json'

const props = defineProps({
  aws_url: {
    type: String,
    required: true
  },
  minDate: String
})

const times = ref<string[]>([])
const unavailableDays = ref<UnavailableDays | null>(null)
const dateError = ref<boolean>(false)
const timeError = ref<boolean>(false)
const disableTimeInput = ref<boolean>(true)

const emit = defineEmits(['updateDate', 'pageLoaded'])
const model = defineModel()
const langContent = useLang(AppointmentDateTimePickerContent)

const handleDateChange = (selectedDate: string) => {
  dateError.value = false
  const unavailableDay = unavailableDays.value?.unavailable.find(({ day }) => day === selectedDate)

  const availableTimes = unavailableDays.value?.times.filter(
    (time) =>
      !unavailableDay?.time.some((unavailableTime) =>
        time.startsWith(unavailableTime.slice(0, 2))
      ) && !unavailableDay?.time.includes(time)
  )

  times.value = availableTimes ?? unavailableDays.value?.times ?? []

  disableTimeInput.value = false

  emit('updateDate', selectedDate)
}

const removeSelectedDate = () => {
  dateError.value = true
  disableTimeInput.value = true
  emit('updateDate', '')
}

const setDateError = () => {
  dateError.value = true
}

const setTimeError = () => {
  timeError.value = true
}

const handleTimeChange = () => {
  timeError.value = false
}

onBeforeMount(async () => {
  const result = await getNotAvailableDays(props.aws_url)
  if (result) {
    unavailableDays.value = result
  }
  emit('pageLoaded')
})

defineExpose({
  setDateError,
  setTimeError
})
</script>

<template>
  <div class="w-full mb-5 flex flex-col items-center mx-auto">
    <app-date-input
      v-if="unavailableDays"
      :unavailableDays="unavailableDays"
      :minDate="minDate"
      @handleDateChange="handleDateChange"
      @handleRemoveSelectedDate="removeSelectedDate"
      :error="dateError"
    />
    <div class="w-full flex flex-col items-center">
      <app-select
        :label="langContent.time_label"
        class="mt-5"
        v-model="model"
        @update:modelValue="handleTimeChange"
        :options="times"
        :error="timeError"
        :disabled="disableTimeInput"
      />
      <span v-if="timeError" class="text-english-vermillion">{{ langContent.time_error }}</span>
    </div>
  </div>
</template>
